/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Soft-fail + Rollbar tracing when ngx-translate would throw
 * `Parameter "key" required` (null / undefined / '').
 */
import {TranslateService} from '@ngx-translate/core';

import {AdnLogService} from '../../../components/util/adn-log.service';

export type MissingTranslateKeyDetails = {
  /** Call-site label, e.g. ogTranslate / AuditHistoryComponent.processMessage */
  source: string;
  key?: any;
  defaultText?: string;
  interpolateParams?: object;
  /** Field that should have supplied the key fragment (code, status, …) */
  missingField?: string;
  messageSummary?: {
    code?: any;
    text?: string;
    type?: string;
    id?: string;
    paramKeys?: string[];
  };
  extra?: Record<string, any>;
};

type RouteContextProvider = () => { href?: string; stateName?: string } | undefined;

const MAX_REPORTS_PER_SIGNATURE = 3;
const MAX_REPORTS_TOTAL = 40;

let adnLog: AdnLogService | null = null;
let getRouteContext: RouteContextProvider | null = null;
let installed = false;
const reportCounts = new Map<string, number>();
let totalReports = 0;

export function isMissingTranslateKey(key: any): boolean {
  if (key == null || key === '') {
    return true;
  }
  if (Array.isArray(key) && key.length === 0) {
    return true;
  }
  return false;
}

export function describeTranslateKey(key: any): {keyType: string; keyRepr: string} {
  if (key === null) {
    return {keyType: 'null', keyRepr: 'null'};
  }
  if (key === undefined) {
    return {keyType: 'undefined', keyRepr: 'undefined'};
  }
  if (key === '') {
    return {keyType: 'empty-string', keyRepr: ''};
  }
  if (Array.isArray(key)) {
    return {keyType: 'array', keyRepr: truncate(safeJson(key), 200)};
  }
  if (typeof key === 'object') {
    return {keyType: typeof key, keyRepr: truncate(safeJson(key), 200)};
  }
  return {keyType: typeof key, keyRepr: truncate(String(key), 200)};
}

/**
 * Wire AdnLogService (and optional route context) so pure helpers can report
 * without injecting Angular services.
 */
export function configureMissingTranslateKeyReporting(
  log: AdnLogService,
  routeContext?: RouteContextProvider
): void {
  adnLog = log;
  getRouteContext = routeContext || null;
}

/**
 * Patch TranslateService.instant so unguarded callers soft-fail and report.
 * Safe to call once at bootstrap.
 */
export function installMissingTranslateKeyReporting(
  translate: TranslateService,
  log: AdnLogService,
  routeContext?: RouteContextProvider
): void {
  configureMissingTranslateKeyReporting(log, routeContext);
  if (installed) {
    return;
  }
  installed = true;

  const originalInstant = translate.instant.bind(translate);
  translate.instant = ((key: any, interpolateParams?: object) => {
    if (isMissingTranslateKey(key)) {
      reportMissingTranslateKey({
        source: 'TranslateService.instant',
        key,
        interpolateParams
      });
      return Array.isArray(key) ? {} : '';
    }
    return originalInstant(key, interpolateParams);
  }) as TranslateService['instant'];
}

export function reportMissingTranslateKey(details: MissingTranslateKeyDetails): void {
  const {keyType, keyRepr} = describeTranslateKey(details.key);
  // Group by call site + key shape; keep a short text prefix so distinct
  // templates stay separable without one unique text per audit row.
  const textPrefix = details.messageSummary?.text == null
    ? ''
    : truncate(String(details.messageSummary.text), 60);
  const signature = [
    details.source,
    keyType,
    keyRepr,
    details.missingField || '',
    details.messageSummary?.type || '',
    textPrefix
  ].join('|');

  if (!shouldReport(signature)) {
    return;
  }

  const route = getRouteContext ? getRouteContext() : undefined;
  const err = new Error('Translate key missing');
  const payload = {
    translateKeyMissing: true,
    source: details.source,
    keyType,
    keyRepr,
    missingField: details.missingField,
    defaultText: details.defaultText == null ? undefined : truncate(String(details.defaultText), 200),
    interpolateParamKeys: details.interpolateParams ? Object.keys(details.interpolateParams) : undefined,
    messageSummary: details.messageSummary
      ? {
          ...details.messageSummary,
          text: details.messageSummary.text == null
            ? undefined
            : truncate(String(details.messageSummary.text), 300)
        }
      : undefined,
    href: route?.href,
    stateName: route?.stateName,
    ...(details.extra || {})
  };

  try {
    if (adnLog) {
      adnLog.warn(err, payload);
    } else {
      // eslint-disable-next-line no-console
      console.warn(err.message, payload);
    }
  } catch (e) {
    // Never let tracing break the UI.
  }
}

export function summarizeTranslateMessage(message: any): MissingTranslateKeyDetails['messageSummary'] {
  if (!message || typeof message !== 'object') {
    return undefined;
  }
  const params = message.parameters || message.params;
  return {
    code: message.code,
    text: message.text,
    type: message.type,
    id: message.id,
    paramKeys: params && typeof params === 'object' ? Object.keys(params) : undefined
  };
}

/** Test helper — clears install state and rate limits. */
export function resetMissingTranslateKeyReportingForTests(): void {
  adnLog = null;
  getRouteContext = null;
  installed = false;
  reportCounts.clear();
  totalReports = 0;
}

function shouldReport(signature: string): boolean {
  if (totalReports >= MAX_REPORTS_TOTAL) {
    return false;
  }
  const count = (reportCounts.get(signature) || 0) + 1;
  reportCounts.set(signature, count);
  if (count > MAX_REPORTS_PER_SIGNATURE) {
    return false;
  }
  totalReports += 1;
  return true;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) + '…' : value;
}

function safeJson(value: any): string {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}
